import { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, FileCheck2, X, Check, Eye, AlertCircle, ArrowRight } from 'lucide-react';
import { HudButton, HudBadge } from '../hud';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/materialtheque`;
const opts = { withCredentials: true };

const VARIABLE_LABELS = {
  sols_principaux: 'Sols principaux',
  sols_exterieurs: 'Sols extérieurs',
  murs_principaux: 'Murs principaux',
  murs_accents: 'Murs accent',
  cuisine_materiaux: 'Cuisine',
  salle_de_bain_materiaux: 'Salle de bain',
  menuiserie: 'Menuiserie',
};

/**
 * MaterialthequePendingBanner — bandeau "CCTP analysé en attente d'intégration"
 * affiché en tête de ProjectDetailPage si pending_materialtheque_job_id existe.
 *
 * Clic "Vérifier & intégrer" → ouvre un drawer de comparaison avant/après.
 */
const MaterialthequePendingBanner = ({ projectId, onIntegrated }) => {
  const [state, setState] = useState({ loading: true, has: false });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const fetchPending = async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/pending`, opts);
      setState({ loading: false, ...r.data });
    } catch (e) {
      setState({ loading: false, has: false });
    }
  };

  useEffect(() => {
    if (!projectId) return;
    fetchPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const integrate = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/projects/${projectId}/integrate-pending`, {}, opts);
      toast.success('Matériauthèque intégrée au prompt du projet', {
        description: 'Les prochaines générations utiliseront les nouveaux matériaux',
      });
      setDrawerOpen(false);
      setState({ loading: false, has: false });
      onIntegrated && onIntegrated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur intégration');
    }
    setBusy(false);
  };

  const dismiss = async () => {
    if (!window.confirm('Ignorer cette mise à jour ? Le projet conservera la configuration actuelle.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/projects/${projectId}/dismiss-pending`, {}, opts);
      toast.message('Mise à jour ignorée');
      setDrawerOpen(false);
      setState({ loading: false, has: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
    setBusy(false);
  };

  if (state.loading || !state.has_pending) return null;

  const total = state.draft?.summary?.total ?? 0;
  const fileName = state.draft?.source_file_name || 'CCTP';
  const sections = state.draft?.summary?.sections || [];
  const draftValues = state.draft?.values || {};
  const activeValues = state.active?.values || {};

  return (
    <>
      <div
        className="hud-fade-in mt-1 mb-5 mx-4 sm:mx-6 lg:mx-8 relative overflow-hidden"
        data-testid="materialtheque-pending-banner"
        style={{
          background: 'linear-gradient(135deg, rgba(217,160,91,0.12) 0%, rgba(217,160,91,0.04) 100%)',
          border: '1px solid var(--c-border-accent)',
          borderRadius: 16,
          padding: 16,
          boxShadow: 'var(--shadow-glow)',
        }}
      >
        {/* Glow halo */}
        <span
          aria-hidden
          className="absolute -top-16 -right-16 w-48 h-48 rounded-full pointer-events-none"
          style={{ background: 'rgba(217,160,91,0.20)', filter: 'blur(50px)' }}
        />

        <div className="relative z-[1] flex flex-col sm:flex-row sm:items-center gap-4">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
            style={{
              background: 'var(--c-gold)',
              boxShadow: '0 0 20px rgba(217,160,91,0.45)',
            }}
          >
            <Sparkles className="w-5 h-5 text-black animate-pulse" strokeWidth={2} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="hud-eyebrow hud-eyebrow-gold">CCTP analysé</span>
              <HudBadge tone="warning" icon={AlertCircle}>
                En attente d'intégration
              </HudBadge>
            </div>
            <p
              className="mt-1.5 font-semibold tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 17 }}
            >
              {total} matériaux extraits depuis <span style={{ color: 'var(--c-gold)' }}>{fileName}</span>
            </p>
            <p className="mt-0.5 text-[12.5px]" style={{ color: 'var(--c-text-2)' }}>
              Les nouvelles générations utilisent l'ancien prompt jusqu'à validation.
              {sections.length > 0 && (
                <span> · {sections.map((s) => `${s.label} (${s.count})`).join(' · ')}</span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <HudButton
              variant="ghost"
              size="sm"
              onClick={() => setDrawerOpen(true)}
              data-testid="materialtheque-banner-review"
              leftIcon={<Eye className="w-3.5 h-3.5" />}
            >
              Vérifier
            </HudButton>
            <HudButton
              variant="primary"
              size="sm"
              onClick={() => setDrawerOpen(true)}
              data-testid="materialtheque-banner-integrate-cta"
              rightIcon={<ArrowRight className="w-3.5 h-3.5" />}
            >
              Intégrer au prompt
            </HudButton>
          </div>
        </div>
      </div>

      {/* Review drawer */}
      {drawerOpen && (
        <ReviewDrawer
          draftValues={draftValues}
          activeValues={activeValues}
          fileName={fileName}
          total={total}
          sections={sections}
          onClose={() => setDrawerOpen(false)}
          onIntegrate={integrate}
          onDismiss={dismiss}
          busy={busy}
        />
      )}
    </>
  );
};

// --------------------------------------------------------------------------

const ReviewDrawer = ({ draftValues, activeValues, fileName, total, sections, onClose, onIntegrate, onDismiss, busy }) => {
  const fields = Object.keys(VARIABLE_LABELS).filter((k) => draftValues[k] || activeValues[k]);

  // Diff stats
  const diffStats = fields.reduce((acc, k) => {
    const draft = draftValues[k] || '';
    const active = activeValues[k] || '';
    if (draft && !active) acc.added += 1;
    else if (draft && active && draft !== active) acc.modified += 1;
    else if (!draft && active) acc.cleared += 1;
    else acc.unchanged += 1;
    return acc;
  }, { added: 0, modified: 0, cleared: 0, unchanged: 0 });
  const totalChanges = diffStats.added + diffStats.modified + diffStats.cleared;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/65 backdrop-blur-sm z-[1400] animate-in fade-in duration-200"
        onClick={onClose}
        data-testid="materialtheque-review-backdrop"
      />
      <aside
        className="fixed top-0 right-0 bottom-0 w-full max-w-[560px] flex flex-col z-[1410] animate-in slide-in-from-right duration-300"
        style={{
          background: 'linear-gradient(to bottom, #0F0F12, #09090B)',
          borderLeft: '1px solid var(--c-border)',
          boxShadow: 'var(--shadow-cockpit)',
        }}
        data-testid="materialtheque-review-drawer"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div
          className="flex items-start justify-between gap-3 px-6 py-5 shrink-0"
          style={{ borderBottom: '1px solid var(--c-border)' }}
        >
          <div className="min-w-0 flex-1">
            <span className="hud-eyebrow hud-eyebrow-gold">Pré-remplissage du prompt</span>
            <h3
              className="mt-1 font-semibold tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 22 }}
            >
              Comparer & intégrer
            </h3>
            <p className="mt-1 text-[12.5px]" style={{ color: 'var(--c-text-2)' }}>
              {total} matériaux · {fileName}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="materialtheque-review-close"
            className="hud-btn hud-btn-ghost hud-btn-sm aspect-square px-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Sections summary */}
        <div className="px-6 pt-4">
          <span className="hud-eyebrow">Sections détectées</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {sections.map((s) => (
              <HudBadge key={s.id} tone="gold">
                {s.label} · {s.count}
              </HudBadge>
            ))}
          </div>
        </div>

        {/* Diff summary */}
        <div className="px-6 pt-4">
          <div
            className="rounded-xl p-3.5 flex items-center gap-3 flex-wrap"
            style={{
              background: totalChanges > 0 ? 'rgba(217,160,91,0.08)' : 'var(--c-surface-1)',
              border: `1px solid ${totalChanges > 0 ? 'var(--c-border-accent)' : 'var(--c-border)'}`,
            }}
            data-testid="materialtheque-review-diff"
          >
            <span
              className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
              style={{
                background: totalChanges > 0 ? 'var(--c-gold-soft)' : 'var(--c-surface-2)',
                border: `1px solid ${totalChanges > 0 ? 'var(--c-border-accent)' : 'var(--c-border)'}`,
                color: totalChanges > 0 ? 'var(--c-gold)' : 'var(--c-text-muted)',
              }}
            >
              <Sparkles className="w-4 h-4" />
            </span>
            <div className="flex-1 min-w-0">
              <span className="hud-eyebrow hud-eyebrow-gold">Estimation de l'écart</span>
              <p className="mt-0.5 text-[13px] leading-snug" style={{ color: 'var(--c-text)' }}>
                {totalChanges === 0 ? (
                  <span style={{ color: 'var(--c-text-muted)' }}>
                    Aucun changement — le prompt actuel correspond déjà au CCTP.
                  </span>
                ) : (
                  <>
                    <span className="hud-tabular font-semibold" style={{ color: 'var(--c-gold)' }}>
                      {totalChanges} variable{totalChanges > 1 ? 's' : ''}
                    </span>{' '}
                    <span style={{ color: 'var(--c-text-2)' }}>seront mises à jour</span>
                    {diffStats.unchanged > 0 && (
                      <span style={{ color: 'var(--c-text-muted)' }}> · {diffStats.unchanged} inchangée{diffStats.unchanged > 1 ? 's' : ''}</span>
                    )}
                  </>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {diffStats.modified > 0 && <DiffPill count={diffStats.modified} label="modifiée" tone="gold" />}
              {diffStats.added > 0    && <DiffPill count={diffStats.added}    label="nouvelle" tone="success" />}
              {diffStats.cleared > 0  && <DiffPill count={diffStats.cleared}  label="vidée"   tone="warning" />}
            </div>
          </div>
        </div>

        {/* Diff body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <span className="hud-eyebrow">Variables du prompt</span>
          {fields.length === 0 && (
            <p className="text-[13px]" style={{ color: 'var(--c-text-muted)' }}>
              Aucun champ pré-rempli pour ce projet.
            </p>
          )}
          {fields.map((k) => {
            const draft = draftValues[k] || '';
            const active = activeValues[k] || '';
            const changed = draft && draft !== active;
            return (
              <div
                key={k}
                className="rounded-xl p-3.5"
                style={{
                  background: changed ? 'rgba(217,160,91,0.05)' : 'var(--c-surface-1)',
                  border: `1px solid ${changed ? 'var(--c-border-accent)' : 'var(--c-border)'}`,
                }}
                data-testid={`mat-review-field-${k}`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-[13px] font-medium" style={{ color: 'var(--c-text)' }}>
                    {VARIABLE_LABELS[k] || k}
                  </span>
                  {changed && <HudBadge tone="gold">Mise à jour</HudBadge>}
                </div>
                {active && (
                  <div className="mb-1.5">
                    <span className="hud-eyebrow" style={{ color: 'var(--c-text-faint)' }}>Ancien</span>
                    <p
                      className="text-[12.5px] leading-relaxed line-through opacity-60"
                      style={{ color: 'var(--c-text-2)' }}
                    >
                      {active}
                    </p>
                  </div>
                )}
                <div>
                  <span className="hud-eyebrow hud-eyebrow-gold">Nouveau</span>
                  <p className="text-[13px] leading-relaxed" style={{ color: 'var(--c-text)' }}>
                    {draft || <em style={{ color: 'var(--c-text-faint)' }}>(vide — non extrait)</em>}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div
          className="px-6 py-4 flex items-center justify-between gap-3 shrink-0"
          style={{ borderTop: '1px solid var(--c-border)' }}
        >
          <HudButton
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            disabled={busy}
            data-testid="materialtheque-review-dismiss"
          >
            Ignorer
          </HudButton>
          <HudButton
            variant="primary"
            size="md"
            onClick={onIntegrate}
            disabled={busy}
            leftIcon={<FileCheck2 className="w-4 h-4" />}
            data-testid="materialtheque-review-integrate"
          >
            {busy ? 'Intégration...' : 'Intégrer au prompt'}
          </HudButton>
        </div>
      </aside>
    </>
  );
};

export default MaterialthequePendingBanner;

const DiffPill = ({ count, label, tone }) => (
  <span
    className="hud-badge inline-flex items-center gap-1"
    style={{
      background: tone === 'gold' ? 'var(--c-gold-soft)' :
                  tone === 'success' ? 'rgba(34,197,94,0.12)' :
                  'rgba(234,179,8,0.12)',
      color: tone === 'gold' ? 'var(--c-gold)' :
             tone === 'success' ? '#86EFAC' :
             '#FDE68A',
      border: `1px solid ${tone === 'gold' ? 'var(--c-border-accent)' :
                            tone === 'success' ? 'rgba(34,197,94,0.3)' :
                            'rgba(234,179,8,0.3)'}`,
    }}
  >
    <span className="hud-tabular font-bold">{count}</span>
    <span>{label}{count > 1 ? 's' : ''}</span>
  </span>
);
